import $ from 'jquery';
import ko from 'lib/knockout';
import _ from 'lib/underscore';
import * as urlUtils from "../../../core/utils/url";
import {ProductItem, ProductList} from "../common/model/products";
import {Settings} from "../common/model/settings";
import * as loggerUtils from "../../../core/utils/logger";
import {BaseList} from "../common/model/lists";
import {ReviewItem, ReviewList} from "../common/model/reviews";
import {settings} from "../common/data";
import {lang} from "../../../core/utils/lang";
import {DebugInfo} from "../common/model/debuginfo";

const logger = loggerUtils.newLogger('Quicksearch');

/** @type {QuickSearchViewModel} */
let model;

// use local debugInfo instance to avoid overriding existing one
// if this code is executing on shop catalog page
const debugInfo = new DebugInfo();

/**
 *
 * @param {object} options
 * @return {QuickSearchViewModel}
 */
export function init(options) {
	logger.log('init', options);
	model = new QuickSearchViewModel();
	model.init(options);
	return model;
}

class QuickSearchViewModel {
	criteria = ko.observable('');
	hasFocus = ko.observable(false);

	isResultsVisible = ko.observable(false);

	/**
	 * @type {Settings}
	 */
	settings = null;
	/**
	 * @type {Results}
	 */
	results = null;

	_xhr = null;
	_debugInfoSearchCriteria = null;

	constructor() {
		this._criteriaTrimmed = ko.pureComputed(() => (this.criteria() || '').trim());
		this._filterRequest = ko.pureComputed(this._criteriaTrimmed).extend({rateLimit: {timeout: 300, method: "notifyWhenChangesStop"}});
		this.settings = settings;
		this.results = new Results(settings);

		this.results.onShowAll(() => {
			this.handleSubmit();
		});
		this.results.onCategoryItemClick(/** @type {CategoryResultItem} */ item => {
			location.href = urlUtils.joinPath(settings.shopUrl, item.uri, {search: this._criteriaTrimmed()});
		});
		this.results.onKeywordItemClick(/** @type {KeywordItem} */ item => {
			if (item.type != 'keyword') {
				location.href = urlUtils.joinPath(settings.shopUrl, {[item.type]: item.id});
			} else {
				location.href = urlUtils.joinPath(settings.shopUrl, {search: item.title});
			}
		});
		this.results.onReviewsShowAllClick(() => {
			location.href = urlUtils.siteUrl('reviews', {search: this._criteriaTrimmed()});
		});

		this.handleSubmit = this.handleSubmit.bind(this);
	}

	get hasCriteria() {
		return !!this._criteriaTrimmed();
	}

	get isActive() {
		return this.hasFocus() || this.isResultsVisible();
	}

	init(options) {
		const o = {
			criteria: null,
			...options
		};

		this.criteria(o.criteria);

		this._filterRequest.subscribe(criteria => {
			this._doQuery(criteria);
		});
		this._criteriaTrimmed.subscribe(query => {
			this._toggleResults(!!query, true);
		});
		this.hasFocus.subscribe(hasFocus => {
			if (hasFocus && this.hasCriteria) {
				this._toggleResults(true);
				if (!this.results.isInitialized()) {
					this._doQuery();
				}
			}
		});

		//test
		// this.hasFocus(true);
	}

	_toggleResults(show, reset = false) {
		if (reset) {
			this.results.reset();
		}
		this.isResultsVisible(show);
	}

	closeResults() {
		this._toggleResults(false);
	}

	_doQuery() {
		const criteria = '' + this._criteriaTrimmed();
		logger.log("Do search", criteria);

		if (this._xhr) {
			this._xhr.abort();
		}

		if (!criteria || criteria.length < 3) {
			return;
		}

		this._xhr = $.get(settings.searchApiUrl, {criteria}, response => {
			logger.log("new data", response);
			debugInfo.setSearchCriteria(criteria);
			this.results.init(response ? response : {error: true});
		}, 'json').fail((xhr, status) => {
			if (status != 'abort') {
				this.results.init({error: true});
			}
		}).always(() => {
			this._xhr = null;
		});

		if(window.fbq) {
            fbq('track', 'Search', {
                search_string: criteria,
            });
		}
	}

	handleSubmit() {
		location.href = urlUtils.joinPath(settings.shopUrl, {search: this._criteriaTrimmed()});
	}

	reset() {
		location.href = urlUtils.joinPath(settings.shopUrl);
	}
}

class Results {
	/** @type {ProductResults} */
	products = null;
	categories = new CategoryResults();
	keywords = new KeywordResults();
	/** @type {ReviewResults} */
	reviews = null;

	isInitialized = ko.observable(false);
	isEmpty = ko.pureComputed(() => this.products.empty && this.reviews.empty);
	isNotEmpty = ko.pureComputed(() => !this.isEmpty());
	error = ko.observable();

	isLoading = ko.pureComputed(() => !this.isInitialized());
	isResultsVisible = ko.pureComputed(() => this.isInitialized() && this.isNotEmpty());

	_onShowAll = $.Callbacks();

	constructor(settings) {
		this.products = new ProductResults(settings);
		this.reviews = new ReviewResults(settings);
	}

	init(data) {
		this.error(data.error);
		if (!data.error) {
			this.products.init({
				items: data.products || [],
				scores: data.productsScores,
				total: data.productsTotalCount
			});
			this.categories.init(data.productCategories || []);
			this.keywords.init(data);
			this.reviews.init({
				items: data.reviews || [],
				scores: data.reviewsScores,
				total: data.reviewsTotalCount
			});
		}
		this.isInitialized(true);
	}

	reset() {
		this.isInitialized(false);
	}

	onCategoryItemClick(callback) {
		this.categories.onItemClick(callback);
	}

	onReviewsShowAllClick(callback) {
		this.reviews.onShowAll(callback);
	}

	onKeywordItemClick(callback) {
		this.keywords.onItemClick(callback);
	}

	onShowAll(callback) {
		this._onShowAll.add(callback);
	}

	handleShowAllClick() {
		this._onShowAll.fire();
	}
}

class CategoryResults extends BaseList {
	newItem(itemData) {
		return new CategoryResultItem(itemData);
	}
}

class CategoryResultItem {
	constructor(data) {
		this._data = data;
		if (data['subcategory_name']) {
			this._uri = urlUtils.joinPath(data['category_name'], data['subcategory_name']);
		} else {
			this._uri = data['category_name'];
		}
	}

	get categoryTitle() {
		return this._data['category_title'];
	}

	get subcategoryTitle() {
		return this._data['subcategory_title'];
	}

	get count() {
		return this._data['count'];
	}

	get uri() {
		return this._uri;
	}
}

class KeywordResults extends BaseList {
	init(data) {
		let items = [];

		if (data.shops && data.shops.length > 0) {
			const item = data.shops[0];
			items.push({
				...item,
				type: 'shop'
			});
		}
		if (data.brands && data.brands.length > 0) {
			const item = data.brands[0];
			items.push({
				...item,
				type: 'brand'
			});
		}
		if (data.keywords) {
			_.each(data.keywords, item => {
				items.push({
					...item,
					type: 'keyword'
				});
			});
		}

		return super.init(items);
	}

	newItem(itemData) {
		return new KeywordItem(itemData);
	}
}

class KeywordItem {
	constructor(data) {
		this._data = data;
	}

	get id() {
		return this._data['id'];
	}

	get title() {
		return this._data['title'];
	}

	get type() {
		return this._data['type'];
	}

	get typeName() {
		switch (this.type) {
			case 'shop':
				return lang('shop_filter_shop');
			case 'brand':
				return lang('shop_filter_brand');
			default:
				return null;
		}
	}
}

class ProductResults extends ProductList {
	newItem(itemData) {
		return new ProductResultItem(itemData, this.settings);
	}
}

class ProductResultItem extends ProductItem {
	showDebugInfo() {
		if (settings.debug && this.score) {
			debugInfo.show('product', this.id);
		}
	}
}

class ReviewResults extends ReviewList {
	_onShowAll = $.Callbacks();

	onShowAll(callback) {
		this._onShowAll.add(callback);
	}

	showAll() {
		this._onShowAll.fire();
	}

	newItem(itemData) {
		return new ReviewResultItem(itemData, this.settings);
	}
}

class ReviewResultItem extends ReviewItem {
	showDebugInfo() {
		if (settings.debug && this.score) {
			debugInfo.show('review', this.id);
		}
	}
}

ko.components.register('qs-results', {
	template: require('./templates/results.html')
});

ko.components.register('qs-products', {
	template: require('./templates/products.html')
});

ko.components.register('qs-categories', {
	template: require('./templates/categories.html')
});

ko.components.register('qs-keywords', {
	template: require('./templates/keywords.html')
});

ko.components.register('qs-reviews', {
	template: require('./templates/reviews.html')
});



// WEBPACK FOOTER //
// ./modules/shop/quicksearch/viewmodel.js